import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  Button,
  CircularProgress,
  Fade,
  FormControlLabel,
  IconButton,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Fragment, useContext, useRef, useState, useEffect } from 'react';
import { ExpandMore } from '@material-ui/icons';
import SupplierFilter from '../../components/SupplierFilter/SupplierFilter';
import PostedBillsGrid from './PostedBillsGrid';
import PendingBillsGrid from './PendingBillsGrid';
import { LoginContext } from '../../contexts/LoginContext';
import ViewPortGridWrapper from '../../containers/ViewPortGridWrapper';
import { commonFilter, separations } from '../../constants/StyleConstants';
import useCheckingAccountPresenter from './CheckingAccountPresenter';
import CustomDatePicker from '../../components/UI/CustomDatePicker';
import DefaultUnrederedGridMessage from '../../containers/DefaultUnrenderedGridMessage';
import SearchIcon from '@material-ui/icons/Search';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import { parseCLDR26 } from '../../utils/GridUtils';
import { Bold } from '../../components/UI/TextFormatters';
import clsx from 'clsx';

import ListAltIcon from '@material-ui/icons/ListAlt';
import AnimatedButton from '../../components/UI/AnimatedButton';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 0,
  },
  ...commonFilter,
  ...separations,
  accordionLabel: {
    margin: 0,
    minHeight: 'unset !important',
    '&.Mui-expanded': {
      margin: 0,
      background: 'linear-gradient(rgba(0,0,0,0.1), rgba(255,255,255,1))',
      borderRadius: 5,
    },
  },
  visualizationOptionsWrapper: props => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    pointerEvents: props.blockedByLoading ? 'none' : undefined,
    opacity: props.blockedByLoading ? 0.5 : undefined,
    transition: 'all .5s',
  }),
  visualizationOptions: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    columnGap: '1em',
    padding: '0 1em 1em 1em',
  },
  radioButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'row',
  },
  calendarFilters: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    columnGap: '1em',
    marginBottom: '.2em',
  },
  filterSearchButton: {
    marginBottom: '.4em',
    fontSize: '1.5em',
  },
  filtersSummaryWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    columnGap: '1em',
  },
  summaryLoader: {
    position: 'absolute',
    backgroundColor: 'rgba(255,255,255,.7)',
    zIndex: theme.zIndex.drawer + 1,
  },
  summaryCell: {
    fontSize: '.9em',
  },
}));

const Filters = ({ selectedSupplier, setSelectedSupplier, validateAndSearch, cleanFilters }) => {
  const classes = useStyles();
  const { isSupplier } = useContext(LoginContext);

  return (
    <div className={classes.filters}>
      {!isSupplier && (
        <Fragment>
          <SupplierFilter value={selectedSupplier} setValue={setSelectedSupplier} />
          <div className={classes.filtersActionButton}>
            <Button variant="outlined" color="primary" onClick={validateAndSearch}>
              Filtrar
            </Button>
            <Button variant="outlined" color="secondary" onClick={cleanFilters}>
              Limpiar
            </Button>
          </div>
        </Fragment>
      )}
    </div>
  );
};

const SearchTypes = ({
  SEARCH_TYPES,
  dateFrom,
  setDateFrom,
  dateTo,
  setDateTo,
  searchType,
  setSearchType,
  validateSearchTypeAndSearch,
  blockedByLoading,
  handleExport,
  loadingExport,
}) => {
  const classes = useStyles({ blockedByLoading: blockedByLoading });
  const { isAllowedByRole, isSupplier } = useContext(LoginContext);

  const cleanDates = () => {
    setDateFrom(null);
    setDateTo(null);
  };
  const [loadingExportAll, setLoadingExportAll] = useState(false);

  useEffect(() => {
    !loadingExport && setLoadingExportAll(false);
  }, [loadingExport]);

  const exportAll = () => {
    setLoadingExportAll(true);
    handleExport();
  };
  return (
    <div className={classes.visualizationOptionsWrapper}>
      <div className={classes.visualizationOptions}>
        <RadioGroup
          className={classes.radioButtonContainer}
          aria-label="receivers"
          name="receivers"
          value={searchType}
          onChange={e => setSearchType(parseInt(e.target.value))}>
          <FormControlLabel value={SEARCH_TYPES.LAST_2_MONTHS} control={<Radio color="primary" />} label="Ultimos 2 meses" />
          <FormControlLabel value={SEARCH_TYPES.OUTSTANDING_BALANCE} control={<Radio color="primary" />} label="Con saldo pendiente" />
          <FormControlLabel value={SEARCH_TYPES.BY_DATE} control={<Radio color="primary" />} label="Por período" />
        </RadioGroup>
        <div className={classes.calendarFilters}>
          <CustomDatePicker
            disabled={searchType != SEARCH_TYPES.BY_DATE}
            value={dateFrom}
            maxDate={dateTo}
            label="Fecha desde"
            setValue={setDateFrom}
          />
          <CustomDatePicker
            disabled={searchType != SEARCH_TYPES.BY_DATE}
            value={dateTo}
            minDate={dateFrom}
            label="Fecha hasta"
            setValue={setDateTo}
          />
        </div>
        <Tooltip title="Buscar selección" enterDelay={1000}>
          <Button
            disabled={searchType != SEARCH_TYPES.BY_DATE}
            className={classes.filterSearchButton}
            variant="contained"
            color="primary"
            onClick={validateSearchTypeAndSearch}>
            <SearchIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Borrar Filtro" enterDelay={1000}>
          <Button
            disabled={searchType != SEARCH_TYPES.BY_DATE}
            className={classes.filterSearchButton}
            variant="contained"
            color="secondary"
            onClick={cleanDates}>
            <HighlightOffOutlinedIcon />
          </Button>
        </Tooltip>
        <Tooltip title="Exportar" enterDelay={1000}>
          <AnimatedButton
            className={classes.filterSearchButton}
            variant="contained"
            color="primary"
            size="medium"
            fontSize="small"
            loading={loadingExportAll}
            onClick={() => isAllowedByRole() && exportAll()}>
              <ListAltIcon />
          </AnimatedButton>
        </Tooltip>
      </div>
    </div>
  );
 
};

const Summary = ({ loading, data }) => {
  const classes = useStyles();

  const ValueCell = ({ value, ...restProps }) => {
    const cellValue = value || 0;

    return <TableCell {...restProps} className={classes.summaryCell} align="right">{`ARS ${parseCLDR26(cellValue)}`}</TableCell>;
  };

  return (
    <Fade in={true} timeout={500}>
      <div style={{ position: 'relative' }}>
        <Backdrop className={classes.summaryLoader} open={!data || loading}>
          <CircularProgress stlye={{ position: 'absolute' }} color="primary" />
        </Backdrop>
        <TableContainer style={{ width: 'auto' }} className={classes.bottomSeparation} component={Paper}>
          <Table size="small" aria-label="a dense table">
            <TableBody>
              <TableRow key="debts">
                <TableCell className={classes.summaryCell} component="tr" scope="row">
                  <Bold>Deuda vencida</Bold>
                </TableCell>
                <ValueCell value={data?.totalExpired} />
                <TableCell className={classes.summaryCell} component="tr" scope="row">
                  <Bold>Deuda a vencer</Bold>
                </TableCell>
                <ValueCell value={data?.totalToExpire} />
              </TableRow>
              <TableRow key="total">
                <TableCell className={classes.summaryCell} component="tr" scope="row">
                  <Bold>Total de la deuda</Bold>
                </TableCell>
                <ValueCell value={data?.totalDue} />
                <TableCell className={classes.summaryCell} component="tr" scope="row"></TableCell>
                <TableCell className={classes.summaryCell} component="tr" scope="row"></TableCell>
              </TableRow>
              <TableRow key="debtTypes">
                <TableCell className={classes.summaryCell} component="tr" scope="row">
                  <Bold>Deuda con pago asignado</Bold>
                </TableCell>
                <ValueCell value={data?.withAssignedPayment} />
                <TableCell className={classes.summaryCell} component="tr" scope="row">
                  <Bold>Deuda sin pago asignado</Bold>
                </TableCell>
                <ValueCell value={data?.noAssignedPayment} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Fade>
  );
};

const CheckingAccount = () => {
  const classes = useStyles();
  const { isSupplier } = useContext(LoginContext);

  const tableRef = useRef();
  const {
    firstLoad,
    summary,
    postedBillsRows,
    blockedByDateSelection,
    pendingBillsRows,
    loadingPending,
    loadingSummary,
    filtersProps,
    postedBillsActions,
  } = useCheckingAccountPresenter(tableRef);

  return (
    <Fragment>
      <div className={classes.filtersSummaryWrapper}>
        <Filters {...filtersProps} />
        {!firstLoad && <Summary loading={loadingSummary} data={summary} />}
      </div>
      <ViewPortGridWrapper>
        <DefaultUnrederedGridMessage render={isSupplier || !firstLoad}>
          <Accordion defaultExpanded>
            <AccordionSummary
              className={classes.accordionLabel}
              expandIcon={<ExpandMore />}
              aria-label="Expand"
              aria-controls="additional-actions1-content"
              id="additional-actions1-header">
              <Typography>Facturas contabilizadas</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.root}>
              <SearchTypes {...postedBillsActions} />
              <PostedBillsGrid data={postedBillsRows} tableRef={tableRef} blockedByDateSelection={blockedByDateSelection} />
            </AccordionDetails>
          </Accordion>
          <Accordion defaultExpanded>
            <AccordionSummary
              className={classes.accordionLabel}
              expandIcon={<ExpandMore />}
              aria-label="Expand"
              aria-controls="additional-actions2-content"
              id="additional-actions2-header">
              <Typography>Facturas pendientes de contabilización</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.root}>
              <PendingBillsGrid data={pendingBillsRows} loading={loadingPending} />
            </AccordionDetails>
          </Accordion>
        </DefaultUnrederedGridMessage>
      </ViewPortGridWrapper>
    </Fragment>
  );
};

export default CheckingAccount;
